/**
 * Visibilité sections ProduitModal — création optimisée (Lot 1) + archétype gelé (legacy).
 */

import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { ARCHETYPE_IDS } from '../config/produitArchetypes.js';
import { getCreationShortcutSections } from '../config/produitCreationVisibility.js';
import { buildArchetypePrefillPatch } from '../utils/produitArchetypePrefill.js';
import { getProduitFormBlockingIssues } from '../utils/produitFormValidation.js';
import { nextDecoupesDisponiblesOnWeightToggle } from '../utils/venteAuPoidsFormUx.js';
import {
  shouldShowProductSection,
  shouldShowStandaloneTva,
  isArchetypeGuidedMode,
} from '../utils/produitModalSections.js';

/**
 * @param {object} params
 * @param {boolean} params.isOpen
 * @param {object | null} params.produit
 * @param {boolean} [params.creationOptimized]
 * @param {string | null} params.archetypeId
 * @param {Record<string, unknown> | null} params.archetypeAnswers
 * @param {object} params.formData
 * @param {Array} params.taillesPlateaux
 * @param {Array} params.options
 * @param {(patch: object) => void} params.setFormData
 * @param {() => void} [params.onRequestChangeArchetype]
 */
export function useProduitArchetypeUi({
  isOpen,
  produit,
  creationOptimized = false,
  archetypeId,
  archetypeAnswers,
  formData,
  taillesPlateaux,
  options,
  setFormData,
  onRequestChangeArchetype,
}) {
  const [enrichExpanded, setEnrichExpanded] = useState(false);
  const [revealedSections, setRevealedSections] = useState(() => new Set());
  const [showAllSections, setShowAllSections] = useState(false);
  const [changeArchetypeConfirmOpen, setChangeArchetypeConfirmOpen] = useState(false);
  const prefillAppliedRef = useRef(false);

  const isCreationMode = Boolean(creationOptimized && !produit?.id && !archetypeId);

  useEffect(() => {
    if (!isOpen) {
      setEnrichExpanded(false);
      setRevealedSections(new Set());
      setShowAllSections(false);
      prefillAppliedRef.current = false;
    }
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen || produit?.id || !archetypeId || prefillAppliedRef.current) return;
    const patch = buildArchetypePrefillPatch(archetypeId, archetypeAnswers ?? {});
    if (Object.keys(patch).length > 0) {
      setFormData((prev) => ({ ...prev, ...patch }));
    }
    prefillAppliedRef.current = true;
  }, [isOpen, produit?.id, archetypeId, archetypeAnswers, setFormData]);

  const sectionFilterOpts = useMemo(
    () => ({
      enrichExpanded,
      revealedSections,
      creationMode: isCreationMode,
      showAllSections,
    }),
    [enrichExpanded, revealedSections, isCreationMode, showAllSections]
  );

  const showSec = useCallback(
    (sectionKey) => shouldShowProductSection(archetypeId, sectionKey, sectionFilterOpts),
    [archetypeId, sectionFilterOpts]
  );

  const showStandaloneTva = shouldShowStandaloneTva(archetypeId, sectionFilterOpts);
  const guidedMode = isArchetypeGuidedMode(archetypeId);

  const lockCompositionToggle =
    guidedMode &&
    (archetypeId === ARCHETYPE_IDS.PLATEAU_PIECES || formData.modeComposition);

  const lockPlancheToggle =
    guidedMode &&
    (archetypeId === ARCHETYPE_IDS.PLANCHE_PERSONNES ||
      archetypeId === ARCHETYPE_IDS.PLANCHE_PERSO ||
      formData.isPlateauParPersonne);

  const lockVenteAuPoidsToggle =
    guidedMode && (archetypeId === ARCHETYPE_IDS.POIDS || formData.venteAuPoids);

  const formBlockingIssues = useMemo(
    () => getProduitFormBlockingIssues(formData, taillesPlateaux),
    [formData, taillesPlateaux]
  );
  const formCanSubmit = formBlockingIssues.length === 0;

  const handleRevealSection = useCallback((sectionKey) => {
    setRevealedSections((prev) => new Set([...prev, sectionKey]));
  }, []);

  const scrollToSection = useCallback((sectionKey) => {
    requestAnimationFrame(() => {
      const el = document.querySelector(`[data-produit-section="${sectionKey}"]`);
      el?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });
  }, []);

  const handleCreationShortcut = useCallback(
    (shortcutId) => {
      const keys = getCreationShortcutSections(shortcutId);
      setRevealedSections((prev) => new Set([...prev, ...keys]));
      if (shortcutId === 'plateau') {
        setFormData((prev) => ({ ...prev, modeComposition: true }));
        window.setTimeout(() => scrollToSection('compositionPieces'), 80);
      } else if (shortcutId === 'planche') {
        setFormData((prev) => ({
          ...prev,
          isPlateauParPersonne: true,
          minPersons: prev.minPersons || 2,
          maxPersons: prev.maxPersons || 20,
        }));
        window.setTimeout(() => scrollToSection('planchePersonnes'), 80);
      } else if (shortcutId === 'poids') {
        setFormData((prev) => ({
          ...prev,
          venteAuPoids: true,
          prixAuPoids: prev.prixAuPoids || prev.prix || '',
          poidsPortionStandard: prev.poidsPortionStandard || 50,
          decoupesDisponibles: nextDecoupesDisponiblesOnWeightToggle(
            prev.decoupesDisponibles
          ),
        }));
        window.setTimeout(() => scrollToSection('venteAuPoids'), 80);
      }
    },
    [setFormData, scrollToSection]
  );

  const handleCreationShortcutToggle = useCallback(
    (shortcutId, checked) => {
      if (checked) {
        handleCreationShortcut(shortcutId);
        return;
      }
      const keys = getCreationShortcutSections(shortcutId);
      setRevealedSections((prev) => {
        const next = new Set(prev);
        keys.forEach((k) => next.delete(k));
        return next;
      });
      if (shortcutId === 'plateau') {
        setFormData((prev) => ({ ...prev, modeComposition: false }));
      } else if (shortcutId === 'planche') {
        setFormData((prev) => ({
          ...prev,
          isPlateauParPersonne: false,
          prixParPersonne: null,
        }));
      } else if (shortcutId === 'poids') {
        setFormData((prev) => ({ ...prev, venteAuPoids: false }));
      }
    },
    [handleCreationShortcut, setFormData]
  );

  /** Sync sections révélées ↔ toggles (création) — bas ↔ haut */
  useEffect(() => {
    if (!isCreationMode || showAllSections) return;
    setRevealedSections((prev) => {
      const next = new Set(prev);
      const apply = (shortcutId, active) => {
        const keys = getCreationShortcutSections(shortcutId);
        keys.forEach((k) => (active ? next.add(k) : next.delete(k)));
      };
      apply('plateau', Boolean(formData.modeComposition));
      apply('planche', Boolean(formData.isPlateauParPersonne));
      apply('poids', Boolean(formData.venteAuPoids));
      return next;
    });
  }, [
    isCreationMode,
    showAllSections,
    formData.modeComposition,
    formData.isPlateauParPersonne,
    formData.venteAuPoids,
  ]);

  const handleShowAllSections = useCallback(() => {
    setShowAllSections(true);
  }, []);

  const handleShowSimplified = useCallback(() => {
    setShowAllSections(false);
    setRevealedSections(new Set());
    setFormData((prev) => ({
      ...prev,
      modeComposition: false,
      isPlateauParPersonne: false,
      venteAuPoids: false,
    }));
  }, [setFormData]);

  const handleChangeArchetypeRequest = useCallback(() => {
    const hasDraft =
      Boolean(formData.nom?.trim()) ||
      Boolean(formData.description?.trim()) ||
      formData.images.length > 0 ||
      options.length > 0;
    if (hasDraft) {
      setChangeArchetypeConfirmOpen(true);
      return;
    }
    onRequestChangeArchetype?.();
  }, [formData, options.length, onRequestChangeArchetype]);

  const confirmChangeArchetype = useCallback(() => {
    onRequestChangeArchetype?.();
  }, [onRequestChangeArchetype]);

  return {
    enrichExpanded,
    setEnrichExpanded,
    revealedSections,
    showAllSections,
    isCreationMode,
    changeArchetypeConfirmOpen,
    setChangeArchetypeConfirmOpen,
    sectionFilterOpts,
    showSec,
    showStandaloneTva,
    guidedMode,
    lockCompositionToggle,
    lockPlancheToggle,
    lockVenteAuPoidsToggle,
    formCanSubmit,
    formBlockingIssues,
    handleRevealSection,
    handleCreationShortcut,
    handleCreationShortcutToggle,
    handleShowAllSections,
    handleShowSimplified,
    handleChangeArchetypeRequest,
    confirmChangeArchetype,
  };
}
